import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useCallback, useContext, useState } from "react";
import useSWRMutation from "swr/mutation";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SurveyModal } from "~/components/common/survey/SurveyModal";
import { useDealSurveys } from "~/hooks/deals/useDealSurveys";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { Deal } from "~/models/Deal";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { canMovingStatus } from "~/models/Staff";
import { FINISHED } from "~/models/Status";
import type { SurveyAnswer } from "~/models/Survey";
import Styles from "./StatusHandleButtons.module.scss";

type Props = {
	alertText: string | null;
	canBackward: boolean;
	deal: Deal;
	forwardDisabled: boolean;
	handleBackwardStatus: (reason: string) => Promise<void>;
	handleForwardStatus: () => Promise<void>;
	handleForwardStatusWithSurvey: (
		surveyAnswers: SurveyAnswer[]
	) => Promise<void>;
	handlePartiallyFinish?: (() => Promise<void>) | null;
	nextStatusId: number;
};

export function StatusHandleReasonButtons({
	canBackward,
	handleForwardStatus,
	handleForwardStatusWithSurvey,
	handleBackwardStatus,
	forwardDisabled,
	alertText,
	nextStatusId,
	deal,
	handlePartiallyFinish = null
}: Props) {
	const account = useContext(ItandiAccountContext);
	const [backwardReason, setBackwardReason] = useState("");
	const { data: surveys } = useDealSurveys().index(deal.id);
	const surveyPresent =
		!account.isAdmin &&
		nextStatusId === FINISHED &&
		surveys !== null &&
		surveys.length !== 0;

	const {
		isOpen: openConfirm,
		handleOpen: handleOpenConfirm,
		handleClose: handleCloseConfirm
	} = useOpenHandler();
	const {
		isOpen: openBackConfirm,
		handleOpen: handleOpenBackConfirm,
		handleClose: handleCloseBackConfirm
	} = useOpenHandler();
	const {
		isOpen: openPartiallyFinishConfirm,
		handleOpen: handleOpenPartiallyFinishConfirm,
		handleClose: handleClosePartiallyFinishConfirm
	} = useOpenHandler();
	const {
		isOpen: openSurveyConfirm,
		handleOpen: handleOpenSurveyConfirm,
		handleClose: handleCloseSurveyConfirm
	} = useOpenHandler();

	const handleAcceptForward = async (): Promise<void> => {
		await handleForwardStatus();
		handleCloseConfirm();
	};

	const handleAcceptForwardWithSurvey = async (
		surveyAnswers: SurveyAnswer[]
	): Promise<void> => {
		await handleForwardStatusWithSurvey(surveyAnswers);
		handleCloseSurveyConfirm();
	};

	const handleAcceptPartiallyFinish = async (): Promise<void> => {
		if (handlePartiallyFinish !== null) {
			await handlePartiallyFinish();
		}
	};

	const onChangeBackwardReason = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
			setBackwardReason(e.target.value);
		},
		[]
	);

	const { trigger, isMutating } = useSWRMutation(
		["status-backward", deal.id],
		async (_key: [string, number], { arg }: { arg: string }) => {
			await handleBackwardStatus(arg);
		}
	);

	const onCloseBackConfirm = (): void => {
		setBackwardReason("");
		handleCloseBackConfirm();
	};
	const onSubmitBackward = async (): Promise<void> => {
		await trigger(backwardReason);
		onCloseBackConfirm();
	};

	return (
		<div className={Styles.StatusHandleButtonRow}>
			{handlePartiallyFinish !== null && (
				<Button
					disabled={!canMovingStatus(account)}
					height="default"
					onClick={handleOpenPartiallyFinishConfirm}
					variant="tertiary"
				>
					工事を依頼せずに途中完了する
				</Button>
			)}
			<div className={Styles.RightButtons}>
				{canBackward ? (
					<button
						className={Styles.BackwardButton}
						disabled={!canMovingStatus(account)}
						onClick={handleOpenBackConfirm}
						type="button"
					>
						ステータスを戻す
					</button>
				) : null}
				<button
					className={Styles.ForwardButton}
					disabled={forwardDisabled || !canMovingStatus(account)}
					onClick={surveyPresent ? handleOpenSurveyConfirm : handleOpenConfirm}
					type="button"
				>
					ステータスを進める
				</button>
			</div>
			<ConfirmModal
				actionText="確定"
				confirmType="save"
				handleAccept={handleAcceptPartiallyFinish}
				handleCancel={handleClosePartiallyFinishConfirm}
				isOpen={openPartiallyFinishConfirm}
			>
				<p className={Styles.ConfirmText}>
					案件を完了扱いにしてよろしいですか？
				</p>
				<Alertv2 variant="warning">
					<Paragraph>
						施工会社には工事が依頼されていない状態で完了扱いになります。
					</Paragraph>
				</Alertv2>
			</ConfirmModal>
			<ConfirmModal
				actionText="確定"
				confirmType="save"
				handleAccept={handleAcceptForward}
				handleCancel={handleCloseConfirm}
				isOpen={openConfirm}
			>
				<p className={Styles.ConfirmText}>ステータスを進めてよろしいですか？</p>
				{alertText ? (
					<Alertv2 variant="info">
						<Paragraph>{alertText}</Paragraph>
					</Alertv2>
				) : null}
			</ConfirmModal>
			{openBackConfirm ? (
				<Modalv2 isOpen onClose={onCloseBackConfirm}>
					<ModalHeader title="ステータスを戻す" />
					<ModalBody>
						<FormGroup variant="vertical">
							<FormControl chip="必須" label="理由">
								<InputText
									onChange={onChangeBackwardReason}
									rows={4}
									value={backwardReason}
								/>
							</FormControl>
						</FormGroup>
						<Alertv2 mt={designTokens["spacing/m"]} variant="warning">
							<Paragraph>入力した理由は施工会社に通知されます。</Paragraph>
						</Alertv2>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								disabled={isMutating}
								onClick={onCloseBackConfirm}
								type="button"
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button
								disabled={!backwardReason.trim() || isMutating}
								loading={isMutating}
								onClick={onSubmitBackward}
								type="button"
								variant="primary"
							>
								ステータスをもどす
							</Button>
						}
					/>
				</Modalv2>
			) : null}
			{surveyPresent ? (
				<SurveyModal
					handleAccept={handleAcceptForwardWithSurvey}
					handleClose={handleCloseSurveyConfirm}
					isOpen={openSurveyConfirm}
					surveys={surveys}
				/>
			) : null}
		</div>
	);
}
